import { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Badge } from "components/ui/badge";
import { Checkbox } from "components/ui/checkbox";
import {
  Robot,
  FloppyDisk,
  SpinnerGap,
  CalendarBlank,
  Signature,
  FileText,
  Clock,
  MagnifyingGlass,
  Question,
  CaretDown,
  CaretRight,
  Warning,
  LinkSimple,
  Tag,
  Plus,
  X,
} from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { FieldExtractionRulesPanel, FieldExtractionRule, AIFallbackConfig } from "./extraction-rules";

interface LinkedQuestion {
  id: number;
  displayName: string;
  categoryName: string;
  answerType: string;
}

interface StandardFieldExtraction {
  enabled: boolean;
  searchHint: string;      // Free text: "laatste pagina, nabij handtekening"
  negativeClause: string;  // "Verwar niet met opmaakdatum"
  extractionRules?: FieldExtractionRule[];  // Calculation rules for this field
  aiFallback?: AIFallbackConfig;            // AI fallback when no rule matches
}

interface ExtractionConfig {
  documentDescription: string;
  naturalLanguageAliases: string[];  // Synonyms for AI document classification
  standardFields: {
    expirationDate: StandardFieldExtraction;
    documentDate: StandardFieldExtraction;
    signStatus: StandardFieldExtraction;
    summary: StandardFieldExtraction;
  };
  aiProvider: "openai" | "claude";
  aiModel: string;
}

interface LabelExtractionConfigProps {
  labelId: number;
  labelName: string;
  autoExpand?: boolean;
}

const DEFAULT_FIELD: StandardFieldExtraction = {
  enabled: false,
  searchHint: "",
  negativeClause: "",
  extractionRules: [],
  aiFallback: undefined,
};

const DEFAULT_CONFIG: ExtractionConfig = {
  documentDescription: "",
  naturalLanguageAliases: [],
  standardFields: {
    expirationDate: { ...DEFAULT_FIELD, enabled: true },
    documentDate: { ...DEFAULT_FIELD, enabled: true },
    signStatus: { ...DEFAULT_FIELD },
    summary: { ...DEFAULT_FIELD, enabled: true },
  },
  aiProvider: "openai",
  aiModel: "gpt-4o",
};

const FIELD_INFO = {
  expirationDate: {
    label: "Vervaldatum",
    icon: Clock,
    description: "Wanneer verloopt dit document?",
    placeholder: "bijv. laatste pagina, nabij 'geldig tot' of 'vervaldatum'",
    negativePlaceholder: "bijv. Verwar niet met opmaakdatum of keuringsdatum",
    supportsRules: true,  // Supports calculation rules (documentDate + X years)
  },
  documentDate: {
    label: "Document datum",
    icon: CalendarBlank,
    description: "Datum van opmaak/uitgifte",
    placeholder: "bijv. eerste pagina, bovenaan, nabij 'datum' of 'opgemaakt op'",
    negativePlaceholder: "bijv. Verwar niet met vervaldatum of ingangsdatum",
    supportsRules: false,  // DocumentDate doesn't support calculation rules
  },
  signStatus: {
    label: "Ondertekening",
    icon: Signature,
    description: "Is het document ondertekend?",
    placeholder: "bijv. laatste pagina, onderaan, nabij handtekening",
    negativePlaceholder: "bijv. Verwar niet met paraaf of stempel",
    supportsRules: false,
  },
  summary: {
    label: "Samenvatting",
    icon: FileText,
    description: "Korte samenvatting van inhoud",
    placeholder: "automatisch (geen hint nodig)",
    negativePlaceholder: "",
    supportsRules: false,
  },
};

export const LabelExtractionConfig = ({
  labelId,
  labelName: _labelName,
  autoExpand = false,
}: LabelExtractionConfigProps) => {
  const { session } = useSession();
  const [isExpanded, setIsExpanded] = useState(autoExpand);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  const [config, setConfig] = useState<ExtractionConfig>(DEFAULT_CONFIG);
  const [linkedQuestions, setLinkedQuestions] = useState<LinkedQuestion[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [newSynonym, setNewSynonym] = useState("");

  // Fetch config when expanded
  useEffect(() => {
    if (isExpanded && labelId) {
      fetchConfig();
      fetchLinkedQuestions();
    }
  }, [isExpanded, labelId]);

  const fetchConfig = async () => {
    if (!session) return;
    setIsLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/label/${labelId}/extraction-config`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const data = await response.json();
        // Merge with defaults, converting old format if needed
        const mergedConfig = { ...DEFAULT_CONFIG };
        if (data.documentDescription) {
          mergedConfig.documentDescription = data.documentDescription;
        }
        if (data.naturalLanguageAliases && Array.isArray(data.naturalLanguageAliases)) {
          mergedConfig.naturalLanguageAliases = data.naturalLanguageAliases;
        }
        if (data.standardFields) {
          for (const key of Object.keys(mergedConfig.standardFields) as Array<keyof typeof mergedConfig.standardFields>) {
            if (data.standardFields[key]) {
              mergedConfig.standardFields[key] = {
                enabled: data.standardFields[key].enabled ?? DEFAULT_FIELD.enabled,
                searchHint: data.standardFields[key].searchHint || data.standardFields[key].locationHint || "",
                negativeClause: data.standardFields[key].negativeClause || "",
                extractionRules: data.standardFields[key].extractionRules || [],
                aiFallback: data.standardFields[key].aiFallback || undefined,
              };
            }
          }
        }
        if (data.aiProvider) mergedConfig.aiProvider = data.aiProvider;
        if (data.aiModel) mergedConfig.aiModel = data.aiModel;
        setConfig(mergedConfig);
      }
    } catch (error) {
      console.error("Error fetching extraction config:", error);
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLinkedQuestions = async () => {
    if (!session) return;
    setQuestionsLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/due-diligence/questions/by-label/${labelId}`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const data = await response.json();
        setLinkedQuestions(data);
      }
    } catch (error) {
      console.error("Error fetching linked questions:", error);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!session) return;
    setIsSaving(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/label/${labelId}/extraction-config`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(config),
        },
        session
      );

      if (response.ok) {
        toast.success("Extractie configuratie opgeslagen");
        setHasChanges(false);
      } else {
        toast.error("Fout bij opslaan configuratie");
      }
    } catch (error) {
      console.error("Error saving extraction config:", error);
      Sentry.captureException(error);
      toast.error("Fout bij opslaan configuratie");
    } finally {
      setIsSaving(false);
    }
  };

  const updateConfig = (updates: Partial<ExtractionConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const updateStandardField = (
    field: keyof ExtractionConfig["standardFields"],
    updates: Partial<StandardFieldExtraction>
  ) => {
    setConfig((prev) => ({
      ...prev,
      standardFields: {
        ...prev.standardFields,
        [field]: { ...prev.standardFields[field], ...updates },
      },
    }));
    setHasChanges(true);
  };

  const enabledFieldsCount = Object.values(config.standardFields).filter(f => f.enabled).length;

  // Synonym management
  const addSynonym = () => {
    const trimmed = newSynonym.trim();
    if (trimmed && !config.naturalLanguageAliases.includes(trimmed)) {
      setConfig((prev) => ({
        ...prev,
        naturalLanguageAliases: [...prev.naturalLanguageAliases, trimmed],
      }));
      setNewSynonym("");
      setHasChanges(true);
    }
  };

  const removeSynonym = (synonym: string) => {
    setConfig((prev) => ({
      ...prev,
      naturalLanguageAliases: prev.naturalLanguageAliases.filter((s) => s !== synonym),
    }));
    setHasChanges(true);
  };

  const handleSynonymKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addSynonym();
    }
  };

  return (
    <div className="border rounded-lg bg-white overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-violet-100 rounded">
            <Robot className="h-4 w-4 text-violet-600" weight="duotone" />
          </div>
          <div className="text-left">
            <div className="text-sm font-medium">AI Extractie Configuratie</div>
            <div className="text-xs text-neutral-secondary-mid">
              {enabledFieldsCount > 0
                ? `${enabledFieldsCount} veld${enabledFieldsCount !== 1 ? "en" : ""} actief`
                : "Niet geconfigureerd"}
              {linkedQuestions.length > 0 && (
                <span className="text-violet-600 ml-1">
                  • {linkedQuestions.length} DD vraag{linkedQuestions.length !== 1 ? "en" : ""}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
              Niet opgeslagen
            </Badge>
          )}
          {isExpanded ? (
            <CaretDown className="h-5 w-5 text-neutral-secondary-mid" />
          ) : (
            <CaretRight className="h-5 w-5 text-neutral-secondary-mid" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <SpinnerGap className="h-6 w-6 animate-spin text-violet-600" />
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {/* Section 1: Document Recognition Training - Help AI classify documents */}
              <div className="space-y-4 bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Tag className="h-4 w-4 text-amber-600" weight="duotone" />
                    <Label className="text-sm font-semibold text-amber-800">
                      🎯 Document Herkenning Training
                    </Label>
                  </div>
                  <p className="text-xs text-amber-700">
                    Leer de AI dit documenttype te herkennen en classificeren. Dit bepaalt welk label automatisch wordt toegekend bij upload.
                  </p>
                </div>

                {/* Synonyms/Aliases */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-amber-800">
                    Synoniemen & alternatieve benamingen
                  </Label>
                  <p className="text-xs text-amber-600">
                    Welke andere namen heeft dit document? (bijv. "Elektriciteitskeuring" voor "Elektriciteit keuringsattest")
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {config.naturalLanguageAliases.map((synonym) => (
                      <Badge
                        key={synonym}
                        variant="secondary"
                        className="bg-amber-100 text-amber-800 border-amber-300 gap-1 pr-1"
                      >
                        {synonym}
                        <button
                          onClick={() => removeSynonym(synonym)}
                          className="hover:bg-amber-200 rounded p-0.5 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {config.naturalLanguageAliases.length === 0 && (
                      <span className="text-xs text-amber-500 italic">
                        Nog geen synoniemen toegevoegd
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newSynonym}
                      onChange={(e) => setNewSynonym(e.target.value)}
                      onKeyDown={handleSynonymKeyDown}
                      placeholder="Nieuw synoniem..."
                      className="h-8 text-xs flex-1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addSynonym}
                      disabled={!newSynonym.trim()}
                      className="h-8 px-2"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Document Description for Classification */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-amber-800">
                    Document beschrijving (voor classificatie)
                  </Label>
                  <Textarea
                    value={config.documentDescription}
                    onChange={(e) =>
                      updateConfig({ documentDescription: e.target.value })
                    }
                    placeholder="Beschrijf hoe dit documenttype er typisch uitziet. Bijvoorbeeld: 'Dit is een EPC certificaat. Het bevat een energiescore (A-G), een uniek kenmerk, en heeft typisch een Vlaams energieagentschap logo. De score staat prominent bovenaan.'"
                    rows={3}
                    className="text-sm resize-none"
                  />
                  <p className="text-xs text-amber-600">
                    Beschrijf kenmerken waarmee de AI dit document kan herkennen (logo's, typische tekst, layout)
                  </p>
                </div>
              </div>

              {/* Section 2: Linked DD Questions */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <LinkSimple className="h-4 w-4 text-violet-600" weight="duotone" />
                  <Label className="text-sm font-medium">
                    Gekoppelde Due Diligence Vragen
                  </Label>
                </div>

                {questionsLoading ? (
                  <div className="bg-neutral-50 rounded-lg p-4 flex items-center justify-center">
                    <SpinnerGap className="h-4 w-4 animate-spin text-neutral-400" />
                  </div>
                ) : linkedQuestions.length > 0 ? (
                  <div className="bg-violet-50 rounded-lg p-3 space-y-2">
                    {linkedQuestions.map((q) => (
                      <div
                        key={q.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <Question className="h-3.5 w-3.5 text-violet-500" />
                          <span>{q.displayName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700">
                            {q.categoryName}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {q.answerType}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-violet-600 pt-1 border-t border-violet-100">
                      Deze vragen worden automatisch beantwoord bij upload
                    </p>
                  </div>
                ) : (
                  <div className="bg-neutral-50 rounded-lg p-4 text-center">
                    <Question className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
                    <p className="text-sm text-neutral-500">
                      Geen DD vragen gekoppeld aan dit label
                    </p>
                    <p className="text-xs text-neutral-400 mt-1">
                      Ga naar Due Diligence → Categorieën om vragen te koppelen
                    </p>
                  </div>
                )}
              </div>

              {/* Section 3: Standard Fields - Data Extraction from Documents */}
              <div className="space-y-3 bg-violet-50 rounded-lg p-4 border border-violet-200">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MagnifyingGlass className="h-4 w-4 text-violet-600" weight="duotone" />
                    <Label className="text-sm font-semibold text-violet-800">
                      📄 Data Extractie uit Documenten
                    </Label>
                  </div>
                  <p className="text-xs text-violet-700">
                    Welke gegevens moet de AI uit dit documenttype halen? Configureer hier welke velden worden uitgelezen en waar ze te vinden zijn.
                  </p>
                </div>

                <div className="space-y-3">
                  {(Object.keys(FIELD_INFO) as Array<keyof typeof FIELD_INFO>).map((fieldKey) => {
                    const info = FIELD_INFO[fieldKey];
                    const fieldConfig = config.standardFields[fieldKey];
                    const Icon = info.icon;

                    return (
                      <div
                        key={fieldKey}
                        className={`border rounded-lg overflow-hidden transition-colors ${
                          fieldConfig.enabled
                            ? "bg-violet-50/50 border-violet-200"
                            : "bg-neutral-50 border-neutral-200"
                        }`}
                      >
                        {/* Field Header */}
                        <div className="p-3 flex items-start gap-3">
                          <Checkbox
                            id={fieldKey}
                            checked={fieldConfig.enabled}
                            onCheckedChange={(checked) =>
                              updateStandardField(fieldKey, { enabled: checked as boolean })
                            }
                            className="mt-0.5"
                          />
                          <label
                            htmlFor={fieldKey}
                            className="flex-1 cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-violet-600" weight="duotone" />
                              <span className="text-sm font-medium">
                                {info.label}
                              </span>
                              <span className="text-xs text-neutral-secondary-mid">
                                — {info.description}
                              </span>
                            </div>
                          </label>
                        </div>

                        {/* Expanded fields when enabled */}
                        {fieldConfig.enabled && (
                          <div className="px-3 pb-3 pl-9 space-y-3">
                            {/* Search Hint */}
                            <div className="space-y-1">
                              <Label className="text-xs text-neutral-secondary-mid">
                                Waar zoeken? (optioneel)
                              </Label>
                              <Input
                                value={fieldConfig.searchHint}
                                onChange={(e) =>
                                  updateStandardField(fieldKey, { searchHint: e.target.value })
                                }
                                placeholder={info.placeholder}
                                className="h-8 text-xs"
                              />
                              <p className="text-xs text-neutral-400">
                                Tip: combineer locaties en zoekwoorden, bijv. "laatste pagina, nabij 'geldig tot'"
                              </p>
                            </div>

                            {/* Negative Clause */}
                            {info.negativePlaceholder && (
                              <div className="space-y-1">
                                <Label className="text-xs text-neutral-secondary-mid flex items-center gap-1">
                                  <Warning className="h-3 w-3" />
                                  Verwar niet met... (optioneel)
                                </Label>
                                <Input
                                  value={fieldConfig.negativeClause}
                                  onChange={(e) =>
                                    updateStandardField(fieldKey, { negativeClause: e.target.value })
                                  }
                                  placeholder={info.negativePlaceholder}
                                  className="h-8 text-xs"
                                />
                              </div>
                            )}

                            {/* Extraction Rules Panel - only for fields that support rules */}
                            {info.supportsRules && (
                              <div className="pt-2 border-t mt-2">
                                <FieldExtractionRulesPanel
                                  fieldConfig={fieldConfig}
                                  onUpdate={(updates) => updateStandardField(fieldKey, updates)}
                                  fieldName={info.label}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-neutral-secondary-mid bg-blue-50 p-2 rounded">
                  <strong>Let op:</strong> De AI probeert altijd zelf te zoeken, ook als het niet direct op de aangegeven locatie staat.
                  De hints helpen de AI om sneller de juiste informatie te vinden.
                </p>
              </div>

              {/* Advanced Settings Toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-neutral-secondary-mid hover:text-neutral-primary flex items-center gap-1"
              >
                {showAdvanced ? (
                  <CaretDown className="h-3 w-3" />
                ) : (
                  <CaretRight className="h-3 w-3" />
                )}
                Geavanceerde instellingen
              </button>

              {showAdvanced && (
                <div className="bg-neutral-50 rounded-lg p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">AI Provider</Label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => updateConfig({ aiProvider: "openai", aiModel: "gpt-4o" })}
                          className={`flex-1 px-3 py-1.5 text-xs rounded border transition-colors ${
                            config.aiProvider === "openai"
                              ? "bg-violet-100 border-violet-300 text-violet-700"
                              : "bg-white border-neutral-200 hover:bg-neutral-50"
                          }`}
                        >
                          OpenAI
                        </button>
                        <button
                          onClick={() => updateConfig({ aiProvider: "claude", aiModel: "claude-sonnet-4-20250514" })}
                          className={`flex-1 px-3 py-1.5 text-xs rounded border transition-colors ${
                            config.aiProvider === "claude"
                              ? "bg-violet-100 border-violet-300 text-violet-700"
                              : "bg-white border-neutral-200 hover:bg-neutral-50"
                          }`}
                        >
                          Claude
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">AI Model</Label>
                      <div className="flex gap-2">
                        {config.aiProvider === "openai" ? (
                          <>
                            <button
                              onClick={() => updateConfig({ aiModel: "gpt-4o" })}
                              className={`flex-1 px-3 py-1.5 text-xs rounded border transition-colors ${
                                config.aiModel === "gpt-4o"
                                  ? "bg-violet-100 border-violet-300 text-violet-700"
                                  : "bg-white border-neutral-200 hover:bg-neutral-50"
                              }`}
                            >
                              GPT-4o
                            </button>
                            <button
                              onClick={() => updateConfig({ aiModel: "gpt-4o-mini" })}
                              className={`flex-1 px-3 py-1.5 text-xs rounded border transition-colors ${
                                config.aiModel === "gpt-4o-mini"
                                  ? "bg-violet-100 border-violet-300 text-violet-700"
                                  : "bg-white border-neutral-200 hover:bg-neutral-50"
                              }`}
                            >
                              Mini
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => updateConfig({ aiModel: "claude-sonnet-4-20250514" })}
                              className={`flex-1 px-3 py-1.5 text-xs rounded border transition-colors ${
                                config.aiModel === "claude-sonnet-4-20250514"
                                  ? "bg-violet-100 border-violet-300 text-violet-700"
                                  : "bg-white border-neutral-200 hover:bg-neutral-50"
                              }`}
                            >
                              Sonnet 4
                            </button>
                            <button
                              onClick={() => updateConfig({ aiModel: "claude-3-5-sonnet-20241022" })}
                              className={`flex-1 px-3 py-1.5 text-xs rounded border transition-colors ${
                                config.aiModel === "claude-3-5-sonnet-20241022"
                                  ? "bg-violet-100 border-violet-300 text-violet-700"
                                  : "bg-white border-neutral-200 hover:bg-neutral-50"
                              }`}
                            >
                              3.5 Sonnet
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end pt-2 border-t">
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                  size="sm"
                  className="gap-1.5"
                >
                  {isSaving ? (
                    <>
                      <SpinnerGap className="h-4 w-4 animate-spin" />
                      Opslaan...
                    </>
                  ) : (
                    <>
                      <FloppyDisk className="h-4 w-4" />
                      Configuratie Opslaan
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LabelExtractionConfig;
